/**
 * Map Configuration for Different Geography Types
 * Defines map settings, projections, and data sources for each game mode
 */

import { GameMode } from '../types';

export interface MapConfig {
  geoUrl: string;
  projection: string;
  scale: number;
  center: [number, number];
  minZoom?: number;
  maxZoom?: number;
  initialZoom?: number;
}

// Map data sources
const US_MAP_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
const WORLD_MAP_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

/**
 * Map configurations for each game mode
 */
export const MAP_CONFIGS: Record<GameMode, MapConfig> = {
  // US States Modes
  full_usa: {
    geoUrl: US_MAP_URL,
    projection: 'geoAlbersUsa',
    scale: 1000,
    center: [0, 0],
    minZoom: 0.8,
    maxZoom: 4,
    initialZoom: 1
  },
  northeast: {
    geoUrl: US_MAP_URL,
    projection: 'geoAlbersUsa',
    scale: 1000,
    center: [0, 0],
    minZoom: 1,
    maxZoom: 6,
    initialZoom: 1.5
  },
  mid_atlantic: {
    geoUrl: US_MAP_URL,
    projection: 'geoAlbersUsa',
    scale: 1000,
    center: [0, 0],
    minZoom: 1,
    maxZoom: 6,
    initialZoom: 1.5
  },
  southeast: {
    geoUrl: US_MAP_URL,
    projection: 'geoAlbersUsa',
    scale: 1000,
    center: [0, 0],
    minZoom: 1,
    maxZoom: 6,
    initialZoom: 1.5
  },
  midwest: {
    geoUrl: US_MAP_URL,
    projection: 'geoAlbersUsa',
    scale: 1000,
    center: [0, 0],
    minZoom: 1,
    maxZoom: 6,
    initialZoom: 1.5
  },
  southwest: {
    geoUrl: US_MAP_URL,
    projection: 'geoAlbersUsa',
    scale: 1000,
    center: [0, 0],
    minZoom: 1,
    maxZoom: 6,
    initialZoom: 1.5
  },
  west: {
    geoUrl: US_MAP_URL,
    projection: 'geoAlbersUsa',
    scale: 1000,
    center: [0, 0],
    minZoom: 1,
    maxZoom: 6,
    initialZoom: 1.5
  },

  // Country Modes - Using world map
  north_america: {
    geoUrl: WORLD_MAP_URL,
    projection: 'geoMercator',
    scale: 400,
    center: [-100, 45],
    minZoom: 0.5,
    maxZoom: 8,
    initialZoom: 1
  },
  caribbean: {
    geoUrl: WORLD_MAP_URL,
    projection: 'geoMercator',
    scale: 1500,
    center: [-70, 18],
    minZoom: 0.8,
    maxZoom: 10,
    initialZoom: 1
  },
  south_america: {
    geoUrl: WORLD_MAP_URL,
    projection: 'geoMercator',
    scale: 500,
    center: [-60, -15],
    minZoom: 0.5,
    maxZoom: 8,
    initialZoom: 1
  },
  europe: {
    geoUrl: WORLD_MAP_URL,
    projection: 'geoMercator',
    scale: 600,
    center: [15, 54],
    minZoom: 0.5,
    maxZoom: 8,
    initialZoom: 1
  },
  africa: {
    geoUrl: WORLD_MAP_URL,
    projection: 'geoMercator',
    scale: 450,
    center: [20, 0],
    minZoom: 0.5,
    maxZoom: 8,
    initialZoom: 1
  },
  asia: {
    geoUrl: WORLD_MAP_URL,
    projection: 'geoMercator',
    scale: 400,
    center: [85, 30],  // Balanced position for Asia and Middle East
    minZoom: 0.5,
    maxZoom: 8,
    initialZoom: 1
  }
};

/**
 * Get map configuration for a specific game mode
 */
export function getMapConfig(gameMode: GameMode): MapConfig {
  return MAP_CONFIGS[gameMode] || MAP_CONFIGS.full_usa;
}

/**
 * Check if a game mode uses the world map
 */
export function usesWorldMap(gameMode: GameMode): boolean {
  const countryModes: GameMode[] = ['north_america', 'caribbean', 'south_america', 'europe', 'africa', 'asia'];
  return countryModes.includes(gameMode);
}
